import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { Label } from './label';

type LayoutOrientation = 'vertical' | 'horizontal';

const Content = styled.div``;
const ActionIcon = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--icon-secondary);
`;
const ItemWrapper = styled.div<{
  $layout: LayoutOrientation;
  $actionable: boolean;
  $disabled?: boolean;
}>`
  display: flex;
  align-items: start;
  border: 1px solid var(--field-border-default);
  border-radius: var(--border-radius-small);
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  position: relative;
  ${Label},
  ${Content} {
    padding-top: var(--spacing-05);
    padding-bottom: var(--spacing-05);
    flex: 1 0 50%;
    overflow: hidden;
  }
  ${Label} {
    padding-left: var(--spacing-06);
  }
  --right-padding: calc(var(--spacing-06) + calc(var(--spacing-05) + 16px));
  ${({ $layout }) =>
    $layout === 'horizontal'
      ? css`
          flex-direction: row;
          gap: var(--spacing-05);
          ${Content} {
            padding-right: var(--right-padding);
          }
        `
      : css`
          flex-direction: column;
          align-items: stretch;
          ${Label} {
            padding-right: var(--right-padding);
            border-bottom: 1px solid var(--field-border-default);
          }
          ${Content} {
            padding-right: var(--spacing-06);
            padding-left: var(--spacing-06);
          }
        `};
  ${({ $actionable }) =>
    $actionable
      ? css`
          cursor: pointer;
          &:hover {
            border-color: var(--field-border-hover);
          }
        `
      : ''}
  ${ActionIcon} {
    position: absolute;
    top: var(--spacing-05);
    right: var(--spacing-06);
  }
`;

interface ItemProps {
  className?: string;
  layout?: LayoutOrientation;
  actionIcon?: ReactNode | false;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
}
const ItemComponent: FC<PropsWithChildren<ItemProps>> = ({
  children,
  className,
  layout = 'horizontal',
  disabled = false,
  actionIcon = <FontAwesomeIcon icon={faChevronRight} size="sm" />,
  onClick,
}) => {
  return (
    <ItemWrapper
      className={className}
      $layout={layout}
      $actionable={onClick != null}
      $disabled={disabled}
      onClick={onClick}
    >
      {children}
      {actionIcon ? <ActionIcon>{actionIcon}</ActionIcon> : null}
    </ItemWrapper>
  );
};

type Item = typeof ItemComponent & {
  Label: typeof Label;
  Content: typeof Content;
};
export const Item: Item = ItemComponent as any;
Item.Label = Label;
Item.Content = Content;

const ExpandedContent = styled(Content)``;
const CollapsibleItemWrapper = styled(Item)<{ $collapsed: boolean }>`
  ${({ $collapsed }) =>
    $collapsed
      ? css`
          ${Content} {
            display: block;
          }
          ${ExpandedContent} {
            display: none;
          }
        `
      : css`
          ${Content} {
            display: none;
          }
          ${ExpandedContent} {
            display: block;
          }
        `};
`;

interface CollapsibleItemItemProps extends ItemProps {
  collasped?: boolean;
  onCollapseChange?: (nextState: boolean) => boolean;
}
const CollapsibleItemComponent: FC<
  PropsWithChildren<CollapsibleItemItemProps>
> = (props) => {
  const { collasped, layout, actionIcon, onCollapseChange } = props;
  const [collapsedState, setCollapsedState] = useState(true);

  useEffect(() => {
    if (collasped != null) {
      setCollapsedState(collasped);
    }
  }, [collasped]);
  const onItemClick = useCallback(() => {
    const canContiue = onCollapseChange?.(!collapsedState);

    if (canContiue !== false) {
      setCollapsedState(!collapsedState);
    }
  }, [collapsedState, onCollapseChange]);
  const layoutState =
    layout != null ? layout : collapsedState ? 'horizontal' : 'vertical';
  const actionIconState =
    actionIcon != null ? (
      actionIcon
    ) : collapsedState ? (
      <FontAwesomeIcon icon={faChevronRight} size="sm" />
    ) : (
      <FontAwesomeIcon icon={faChevronDown} size="sm" />
    );

  return (
    <CollapsibleItemWrapper
      {...props}
      layout={layoutState}
      actionIcon={actionIconState}
      $collapsed={collapsedState}
      onClick={onItemClick}
    />
  );
};

type CollapsibleItem = typeof CollapsibleItemComponent & {
  Label: typeof Label;
  Content: typeof Content;
  ExpandedContent: typeof ExpandedContent;
};
export const CollapsibleItem: CollapsibleItem = CollapsibleItemComponent as any;
CollapsibleItem.Label = Label;
CollapsibleItem.Content = Content;
CollapsibleItem.ExpandedContent = ExpandedContent;
